import { useEffect, useState } from "react";
import {
  getWoodWingImage,
  getWoodWingRetryAt,
  loadWoodWingImages,
  woodWingImagesEnabled,
} from "../api/woodwingImages";

// Waits for the list to settle (scrolling, first measure of the virtualizer)
// so one visible set costs one request, not one per frame.
const DEBOUNCE_MS = 150;

// Looks up the WoodWing images for the terms that are on screen right now, in
// one batch. Pass only the visible terms (e.g. the virtualizer's rows): every
// change of that set fetches what isn't cached yet.
// Returns a lookup: undefined = not known (yet), null = no image.
export default function useWoodWingImages(
  terms: (string | null | undefined)[],
) {
  const [, setResultsVersion] = useState(0);
  const [retryTick, setRetryTick] = useState(0);

  // Derived on every render (not memoised) on purpose: an entry that expired
  // since the last render shows up here as missing again and gets refetched.
  const missing = woodWingImagesEnabled
    ? Array.from(
        new Set(terms.map((term) => term?.trim() ?? "").filter(Boolean)),
      ).filter((term) => getWoodWingImage(term) === undefined)
    : [];
  const missingKey = missing.join("\n");

  useEffect(() => {
    if (!missingKey) return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const debounceTimer = setTimeout(async () => {
      await loadWoodWingImages(missingKey.split("\n"));
      if (cancelled) return;
      // Rate limited or WoodWing down: the terms stay uncached, so try again
      // once the cooldown is over, even if nothing on screen changes.
      const waitMs = getWoodWingRetryAt() - Date.now();
      if (waitMs > 0) {
        retryTimer = setTimeout(() => setRetryTick((tick) => tick + 1), waitMs);
      }
      // Last: outside an event handler React 17 re-renders synchronously here,
      // and that may already run this effect's cleanup (which must see the timer).
      setResultsVersion((version) => version + 1);
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [missingKey, retryTick]);

  return getWoodWingImage;
}
